/* Pre-order badge overlay.
 *
 * Pins the "Open for business / Pre-order now" badge over the hero bottle.
 * The badge is position:fixed and tracks the hero bottle's live bounding box
 * (.hero-video__bottle img). That image is held at opacity:0 by bottle-fly.js
 * while a flying "ghost" does the visible scroll animation, but it keeps its
 * layout box — so its getBoundingClientRect() is the truth for where the
 * bottle currently sits.
 *
 * Re-positions on: resize, orientationchange, load, font swap, element reflow
 * (ResizeObserver on the bottle + hero), and scroll. As the user scrolls down
 * the bottle flies away, so the badge fades out; when scrolled back to the top
 * it re-positions and fades back in.
 *
 * Stamp anchor in the 287x121 artwork: circle centre ~ (61.1, 61.4) →
 * fraction (0.2129, 0.5074) of the badge box. We line that point up with a
 * configurable point on the bottle (data-pos-x / data-pos-y, fractions of the
 * bottle box) and size the badge as data-size x the bottle width.
 */
(function () {
  'use strict';

  var ASPECT = 121 / 287;        // badge height / width
  var ANCHOR_X = 61.1 / 287;     // stamp centre, x fraction of badge box
  var ANCHOR_Y = 61.4 / 121;     // stamp centre, y fraction of badge box

  function num(el, attr, fallback) {
    var v = parseFloat(el.getAttribute(attr));
    return isNaN(v) ? fallback : v;
  }

  function clamp(v, lo, hi) {
    return v < lo ? lo : (v > hi ? hi : v);
  }

  function init() {
    // Only freshly-rendered (not yet reparented) badges.
    var badges = document.querySelectorAll('[data-preorder-badge]:not([data-moved])');
    for (var i = 0; i < badges.length; i++) setupBadge(badges[i]);
  }

  function setupBadge(badge) {
    var section = badge.closest('.shopify-section, [data-wetheme-section-id]') || document;
    var sectionId = section.getAttribute && (section.getAttribute('data-wetheme-section-id') ||
                    section.id) || '';
    var bottle = section.querySelector('.hero-video__bottle img') ||
                 document.querySelector('.hero-video__bottle img');
    if (!bottle) { badge.parentNode && badge.parentNode.removeChild(badge); return; }

    // Reparent to <body> so position:fixed is always relative to the viewport,
    // regardless of any transformed/animated ancestor in the hero. Drop any
    // stale badge from a previous render of the same section (design mode).
    var stale = document.querySelectorAll('body > .hero-preorder-badge[data-moved]' +
      (sectionId ? '[data-section="' + sectionId + '"]' : ''));
    for (var s = 0; s < stale.length; s++) stale[s].parentNode.removeChild(stale[s]);
    badge.setAttribute('data-moved', '');
    if (sectionId) badge.setAttribute('data-section', sectionId);
    document.body.appendChild(badge);

    var sizeFactor = num(badge, 'data-size', 1.15);
    var posX = num(badge, 'data-pos-x', 0.80);
    var posY = num(badge, 'data-pos-y', 0.34);

    var MARGIN = 12;       // min gap from any viewport edge (px)
    var BP_DESKTOP = 990;  // matches the theme's bottle stacking breakpoint
    var BP_MOBILE = 600;

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) badge.classList.add('is-static');

    var revealed = false;
    var pending = false;

    function fadeFor(scrollY) {
      // Fully visible at the top; gone by ~40% of a viewport's scroll.
      var span = window.innerHeight * 0.4 || 1;
      var o = 1 - scrollY / span;
      return o < 0 ? 0 : (o > 1 ? 1 : o);
    }

    function place() {
      pending = false;
      var b = bottle.getBoundingClientRect();
      if (!b.width || !b.height) { badge.style.opacity = '0'; return; }

      var scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      var opacity = Shopify_designMode() ? 1 : fadeFor(scrollY);

      if (opacity <= 0.001) {
        badge.style.opacity = '0';
        badge.style.visibility = 'hidden';
        return;
      }

      var vw = window.innerWidth || document.documentElement.clientWidth;
      var vh = window.innerHeight || document.documentElement.clientHeight;

      // Pick a placement by viewport width. Each yields a target point in the
      // viewport, the badge-local fraction that should land on it, and a width.
      // axFrac/ayFrac are the point inside the badge box aligned to ax/ay
      // (stamp centre = ANCHOR_X/ANCHOR_Y; badge centre = 0.5).
      var w, ax, ay, axFrac, ayFrac;

      if (vw >= BP_DESKTOP) {
        // Desktop: stamp sits on the right side of the bottle, tag points right.
        w = sizeFactor * b.width;
        ax = b.left + posX * b.width;
        ay = b.top + posY * b.height;
        axFrac = ANCHOR_X; ayFrac = ANCHOR_Y;
      } else if (vw >= BP_MOBILE) {
        // Tablet (bottle is centred + stacked): move the badge to the bottle's
        // LEFT — stamp near the left edge, tag reading rightwards across it.
        w = Math.min(sizeFactor, 1.0) * b.width;
        ax = b.left + 0.08 * b.width;
        ay = b.top + posY * b.height;
        axFrac = ANCHOR_X; ayFrac = ANCHOR_Y;
      } else {
        // Mobile: overlay the bottle (upper area), centred on it.
        w = Math.min(b.width * 1.05, vw * 0.86);
        ax = b.left + b.width / 2;
        ay = b.top + posY * b.height;
        axFrac = 0.5; ayFrac = ANCHOR_Y;
      }

      // Never wider than the viewport allows.
      w = Math.min(w, vw - 2 * MARGIN);
      var h = w * ASPECT;

      var left = ax - axFrac * w;
      var top = ay - ayFrac * h;

      // Clamp fully on-screen (priority over the anchor) so it never clips.
      left = clamp(left, MARGIN, Math.max(MARGIN, vw - w - MARGIN));
      top = clamp(top, MARGIN, Math.max(MARGIN, vh - h - MARGIN));

      badge.style.width = w + 'px';
      badge.style.height = h + 'px';
      badge.style.transform = 'translate3d(' + Math.round(left) + 'px,' + Math.round(top) + 'px,0)';
      badge.style.visibility = 'visible';
      badge.style.opacity = String(opacity);

      if (!revealed) {
        revealed = true;
        // next frame so the starting (hidden) state is committed first
        requestAnimationFrame(function () { badge.classList.add('is-revealed'); });
      }
    }

    function schedule() {
      if (pending) return;
      pending = true;
      requestAnimationFrame(place);
    }

    function Shopify_designMode() {
      return !!(window.Shopify && window.Shopify.designMode);
    }

    waitForImage(bottle).then(function () {
      place();
      // a couple of follow-up passes catch late layout (fonts, lazy bottle)
      requestAnimationFrame(place);
      setTimeout(place, 250);
    });

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    window.addEventListener('load', schedule);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);

    if (typeof ResizeObserver === 'function') {
      var ro = new ResizeObserver(schedule);
      ro.observe(bottle);
      var hero = section.querySelector && section.querySelector('.hero-video');
      if (hero) ro.observe(hero);
    }
  }

  function waitForImage(img) {
    return new Promise(function (resolve) {
      if (img.complete && img.naturalWidth !== 0) return resolve();
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-init when the hero section is re-rendered.
  document.addEventListener('shopify:section:load', init);
})();
